import { useState, useEffect } from 'react';
import { Upload, Trash2, Edit2, Save, X, Image as ImageIcon, Video, Loader, Eye, EyeOff } from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card } from '../ui/card';
import { adminFetch, getAuthHeaders } from '../../services/adminApi';

import { PageTitle } from './shared';

const GalleryEditor = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState('');
  const [editData, setEditData] = useState({});
  const [showModal, setShowModal] = useState(null);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/gallery`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setItems(data);
      }
    } catch (error) {
      console.error('Failed to load gallery items:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateTitleFromFilename = (filename) => {
    // Extract filename without extension and format it nicely
    const nameWithoutExt = filename.replace(/\.[^/.]+$/, '');
    return nameWithoutExt
      .replace(/[-_]/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      // Upload the file
      const uploadResponse = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('googleAuthToken')}`
        },
        body: formData
      });

      if (uploadResponse.ok) {
        const uploadData = await uploadResponse.json();
        
        // Auto-create gallery item with default title from filename
        const defaultTitle = generateTitleFromFilename(file.name);
        const newItem = {
          title: defaultTitle,
          description: null,
          media_type: 'image',
          media_url: uploadData.url,
          thumbnail_url: null,
          order: items.length + 1
        };

        // Save to gallery
        const saveResponse = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/gallery`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(newItem)
        });

        if (saveResponse.ok) {
          setMessage('✅ Image uploaded and added to gallery!');
          fetchItems();
          setTimeout(() => setMessage(''), 3000);
        } else {
          const errorData = await saveResponse.json();
          setMessage(`❌ ${errorData.detail || 'Failed to save to gallery'}`);
        }
      } else {
        setMessage('❌ Failed to upload image');
      }
    } catch (error) {
      console.error('Upload error:', error);
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setUploading(false);
      // Reset file input
      event.target.value = '';
    }
  };

  const handleVideoAdd = async () => {
    const url = prompt('Enter YouTube video URL:');
    if (!url) return;

    const embedUrl = convertYouTubeUrl(url);
    const defaultTitle = 'YouTube Video ' + (items.length + 1);

    try {
      const newItem = {
        title: defaultTitle,
        description: null,
        media_type: 'video',
        media_url: embedUrl,
        thumbnail_url: null,
        order: items.length + 1
      };

      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/gallery`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(newItem)
      });

      if (response.ok) {
        setMessage('✅ Video added to gallery!');
        fetchItems();
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setMessage(`❌ ${errorData.detail || 'Failed to add video'}`);
      }
    } catch (error) {
      console.error('Video add error:', error);
      setMessage(`❌ Error: ${error.message}`);
    }
  };

  const convertYouTubeUrl = (url) => {
    if (!url) return '';
    if (url.includes('youtube.com/embed/')) return url;
    
    let videoId = '';
    if (url.includes('youtube.com/watch?v=')) {
      videoId = url.split('watch?v=')[1]?.split('&')[0];
    } else if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    } else if (url.includes('youtube.com/v/')) {
      videoId = url.split('/v/')[1]?.split('?')[0];
    }
    
    if (videoId) return `https://www.youtube.com/embed/${videoId}`;
    return url;
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditData({
      title: item.title,
      description: item.description || '',
      order: item.order,
      show_in_gallery: item.show_in_gallery !== false
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditData({});
  };

  const saveEdit = async (itemId) => {
    try {
      // Send empty string for cleared description (backend converts to null)
      const cleanData = {
        title: editData.title,
        description: editData.description,  // Send "" if cleared, backend handles it
        order: editData.order,
        show_in_gallery: editData.show_in_gallery
      };

      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/gallery/${itemId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(cleanData)
      });

      if (response.ok) {
        setEditingId(null);
        setEditData({});
        // Update local state with new visibility
        setItems(items.map(i => i.id === itemId ? { ...i, ...cleanData } : i));
        setMessage('✅ Updated successfully!');
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setMessage(`❌ ${errorData.detail || 'Failed to update'}`);
      }
    } catch (error) {
      console.error('Update error:', error);
      setMessage(`❌ Error: ${error.message}`);
    }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/gallery/${itemId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setMessage('✅ Deleted successfully!');
        setItems(items.filter(item => item.id !== itemId));
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setMessage(`❌ ${errorData.detail || 'Failed to delete'}`);
      }
    } catch (error) {
      console.error('Delete error:', error);
      setMessage(`❌ Error: ${error.message}`);
    }
  };

  const removeFromGallery = async (item, e) => {
    e.stopPropagation(); // Prevent opening the modal

    if (!window.confirm('Remove this image from Gallery Editor?\n\nYou can re-add it from Media Library.')) {
      return;
    }

    try {
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/gallery/${item.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ in_gallery: false })
      });

      if (response.ok) {
        // Remove from local state since it's no longer in Gallery Editor
        setItems(items.filter(i => i.id !== item.id));
        setMessage('✅ Removed from Gallery Editor');
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setMessage(`❌ ${errorData.detail || 'Failed to remove'}`);
      }
    } catch (error) {
      console.error('Remove error:', error);
      setMessage(`❌ Error: ${error.message}`);
    }
  };

  const toggleWebsiteVisibility = async (item, e) => {
    e.stopPropagation(); // Prevent opening the modal

    try {
      const newVisibility = !item.show_in_gallery;
      const response = await adminFetch(`${process.env.REACT_APP_BACKEND_URL}/api/gallery/${item.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ show_in_gallery: newVisibility })
      });

      if (response.ok) {
        // Update local state
        setItems(items.map(i => i.id === item.id ? { ...i, show_in_gallery: newVisibility } : i));
        setMessage(newVisibility ? '✅ Now visible on website' : '✅ Hidden from website');
        setTimeout(() => setMessage(''), 3000);
      } else {
        const errorData = await response.json();
        setMessage(`❌ ${errorData.detail || 'Failed to update'}`);
      }
    } catch (error) {
      console.error('Toggle visibility error:', error);
      setMessage(`❌ Error: ${error.message}`);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center p-12">
        <Loader className="h-8 w-8 animate-spin text-red-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <PageTitle
        title="Gallery"
        description="Manage photos and videos for the gallery page"
        icon={<ImageIcon className="h-6 w-6" />}
        actions={
          <Button
            onClick={() => document.getElementById('galleryUpload').click()}
            disabled={uploading}
            variant="adminPrimary"
          >
            {uploading ? (
              <>
                <Loader className="h-4 w-4 animate-spin mr-2" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="h-4 w-4 mr-2" />
                Upload
              </>
            )}
          </Button>
        }
      />

      {/* Info Note */}
      <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-3 text-sm text-blue-800 dark:text-blue-300">
        <strong>Gallery Editor</strong> manages which images appear on your public website.
        Use the <strong>Show/Hide</strong> toggle in each card to control website visibility.
        To add more images here, go to <strong>Media Library</strong> and toggle "Add to Gallery".
      </div>

      {/* Hidden file input */}
      <input
        id="galleryUpload"
        type="file"
        accept="image/*"
        multiple={false}
        onChange={handleFileUpload}
        className="hidden"
      />

      {/* Video Add Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleVideoAdd}
          disabled={uploading}
          variant="adminSecondary"
        >
          <Video className="h-4 w-4 mr-2" />
          Add YouTube Video
        </Button>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.includes('✅') ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-200 dark:border-green-800' :
          'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
        }`}>
          {message}
        </div>
      )}

      {/* Gallery Grid */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <span className="text-amber-600">●</span>
          Gallery Items ({items.length})
        </h3>

        {items.length === 0 ? (
          <div className="text-center py-12 bg-stone-50 rounded-lg border border-stone-200">
            <ImageIcon className="h-12 w-12 mx-auto text-stone-400 mb-4" />
            <p className="text-stone-600 dark:text-stone-400">No items in gallery yet</p>
            <p className="text-sm text-stone-500 dark:text-stone-400">Upload images to get started</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((item) => (
              <Card key={item.id} className="admin-card border-stone-200 overflow-hidden hover:shadow-lg hover:border-amber-500 transition-all">
                {/* Media Preview */}
                <div 
                  className="relative aspect-video bg-gray-100 dark:bg-gray-800 cursor-pointer group"
                  onClick={() => setShowModal(item)}
                >
                  {item.media_type === 'video' ? (
                    <div className="w-full h-full flex items-center justify-center bg-black">
                      <Video className="h-12 w-12 text-white opacity-75 group-hover:opacity-100 transition-opacity" />
                    </div>
                  ) : (
                    <img
                      src={item.media_url}
                      alt={item.title}
                      className="w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute top-2 right-2 flex gap-1">
                    <button
                      onClick={(e) => toggleWebsiteVisibility(item, e)}
                      className={`px-2 py-1 text-xs font-semibold rounded flex items-center gap-1 transition-colors cursor-pointer ${
                        item.show_in_gallery
                          ? 'bg-green-600 hover:bg-green-700 text-white'
                          : 'bg-stone-600 hover:bg-stone-700 text-white'
                      }`}
                      title={item.show_in_gallery ? 'Click to hide from website' : 'Click to show on website'}
                    >
                      {item.show_in_gallery ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
                      {item.show_in_gallery ? 'Visible' : 'Hidden'}
                    </button>
                    <button
                      onClick={(e) => removeFromGallery(item, e)}
                      className="px-2 py-1 text-xs font-semibold rounded flex items-center gap-1 transition-colors cursor-pointer bg-stone-800/70 hover:bg-red-600 text-white"
                      title="Remove from Gallery Editor"
                    >
                      <X className="h-3 w-3" />
                    </button>
                    <span className={`px-2 py-1 text-xs font-semibold rounded ${
                      item.media_type === 'video' ? 'bg-purple-600 text-white' : 'bg-blue-600 text-white'
                    }`}>
                      {item.media_type === 'video' ? 'Video' : 'Image'}
                    </span>
                  </div>
                </div>

                {/* Info Section */}
                <div className="p-4">
                  {editingId === item.id ? (
                    // Edit Mode
                    <div className="space-y-3">
                      <div>
                        <Label className="text-xs text-gray-800 dark:text-gray-200 font-medium">Title</Label>
                        <Input
                          value={editData.title}
                          onChange={(e) => setEditData({...editData, title: e.target.value})}
                          className="text-sm border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-800 dark:text-gray-200 font-medium">Description</Label>
                        <Textarea
                          value={editData.description}
                          onChange={(e) => setEditData({...editData, description: e.target.value})}
                          rows={2}
                          className="text-sm border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-gray-800 dark:text-gray-200 font-medium">Order</Label>
                        <Input
                          type="number"
                          value={editData.order}
                          onChange={(e) => setEditData({...editData, order: parseInt(e.target.value)})}
                          className="text-sm border-stone-300 focus:border-amber-500 focus:ring-amber-500/20 bg-background"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id={`show-${item.id}`}
                          checked={editData.show_in_gallery}
                          onChange={(e) => setEditData({...editData, show_in_gallery: e.target.checked})}
                          className="h-4 w-4 rounded border-stone-300 text-amber-600 focus:ring-amber-500"
                        />
                        <Label htmlFor={`show-${item.id}`} className="text-xs cursor-pointer text-gray-700 dark:text-gray-200">
                          Show on public website
                        </Label>
                        {!editData.show_in_gallery && (
                          <span className="text-xs text-amber-600">(hidden from visitors)</span>
                        )}
                      </div>
                      <div className="flex gap-2 pt-2 border-t border-stone-200">
                        <Button
                          size="sm"
                          onClick={() => saveEdit(item.id)}
                          variant="adminPrimary"
                          className="flex-1"
                        >
                          <Save className="h-3 w-3 mr-1" />
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="adminOutline"
                          onClick={cancelEdit}
                          className="flex-1"
                        >
                          <X className="h-3 w-3 mr-1" />
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <>
                      <h4 className="font-semibold text-sm mb-1 truncate text-gray-900 dark:text-gray-100">{item.title}</h4>
                      {item.description && (
                        <p className="text-xs text-stone-600 dark:text-stone-400 mb-3 line-clamp-2">{item.description}</p>
                      )}
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="adminSecondary"
                          onClick={() => startEdit(item)}
                          className="flex-1"
                        >
                          <Edit2 className="h-3 w-3 mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="adminDestructive"
                          onClick={() => handleDelete(item.id)}
                          className="flex-1"
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Modal for Full View */}
      {showModal && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setShowModal(null)}
        >
          <div className="max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <div className="bg-background rounded-lg overflow-hidden">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="font-semibold">{showModal.title}</h3>
                <Button size="sm" variant="ghost" onClick={() => setShowModal(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <div className="aspect-video bg-black">
                {showModal.media_type === 'video' ? (
                  <iframe
                    src={showModal.media_url}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                ) : (
                  <img
                    src={showModal.media_url}
                    alt={showModal.title}
                    className="w-full h-full object-contain"
                  />
                )}
              </div>
              {showModal.description && (
                <div className="p-4 border-t">
                  <p className="text-sm text-gray-600 dark:text-gray-400">{showModal.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryEditor;
